import StickyTotalAmountBar from "../StickyFooter";
import CartMirrorComponent from "../Loader/CartLoader";
import BillingMirrorItems from "../BillingMirrorItems";
import EmrDetailsComponent from "../EmrDetailsComponent";
import BillingMirrorHeader from "../BillingMirrorHeader";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useSocket } from "@/app/components/socket/SocketProvider";
import MembershipPlans, {isPitchCard, isUsageCard} from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/MemberShipComponent/MembershipPlans";
import MembershipDetailComponent from "../MembershipDetailComponent";
import PitchingDetailComponent from "../PitchingDetailComponent";
import { MEMBERSHIP_SIMULATION_EVENTS, type EmrMirrorCommand,type MembershipMirrorCommand,type PitchingMirrorCommand,} from "../types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ICartDetailsResponseData } from "@/api_framework/api_modals/Cart";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useCartDetailsForMirrorViewAPI } from "@/app/hooks/api_hooks/Cart/useCartDetailsForMirrorViewAPI";
import { MembershipSimulationPopup } from "@/app/components/routes/auth_routes/CartManagement/CartCreationComponent/components/CartControlCenter";

interface BillingViewComponentProps {
  cartID: string;
  mirrorCustomerId?: string | null;
  emrCommand?: EmrMirrorCommand | null;
  membershipCommand?: MembershipMirrorCommand | null;
  pitchingCommand?: PitchingMirrorCommand | null;
  source?: string;
}

const BillingViewComponent = ({
  cartID,
  mirrorCustomerId,
  emrCommand,
  membershipCommand,
  pitchingCommand,
  source,
}: BillingViewComponentProps) => {
  const { execute: fetchCartDetaills } = useCartDetailsForMirrorViewAPI();
  const [membershipSimulation, setMembershipSimulation] = useState<any | null>(null);
  const lastSimulationMessageRef = useRef<any>(null);
  const simulationScrollRef = useRef<HTMLDivElement>(null);
  const userEmailId = useAppSelector((state) => state.auth?.user?.email);
  const mappedClinicId = useAppSelector((state) => state.root.UIStates.mappedClinicId);
  const mappedClinicName = useAppSelector((state) => state.root.UIStates.mappedClinicName);
  const [loading, setLoading] = useState(false);
  const [cartData, setCartData] = useState<ICartDetailsResponseData | null>(null);
  const { execute: logAPI } = useClickLogAPI();
  const prevFetchKeyRef = useRef<string | null>(null);
  const fetchCartDetailsRef = useRef(fetchCartDetaills);
  const resolvedCustomerId = mirrorCustomerId;
  const activeSessionKeyRef = useRef<string | null>(null);
  const mirrorContainerRef = useRef<HTMLDivElement>(null);
  const mirrorScrollRef = useRef<HTMLDivElement>(null);
  const emrScrollRef = useRef<HTMLDivElement>(null);
  const membershipDemoScrollRef = useRef<HTMLDivElement | null>(null);
  const membershipDetailScrollRef = useRef<HTMLDivElement | null>(null);
  const suppressEmrScrollSyncRef = useRef(false);
  const suppressEmrScrollSyncRafRef = useRef<number | null>(null);
  const { lastJsonMessage: socketJsonMessage, lastMessage: socketRawMessage } = useSocket();
  const [resolvedScrollMessage, setResolvedScrollMessage] = useState<any | null>(null);
  const [emrModalOpen, setEmrModalOpen] = useState(false);
  const [emrId, setEmrId] = useState<string | null>(null);
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);
  const [selectedMembershipId, setSelectedMembershipId] = useState<string | null>(null);
  const [membershipSummary, setMembershipSummary] = useState<any | null>(null);
  const [selectedPitchingId, setSelectedPitchingId] = useState<string | null>(null);

  const membershipCards: any[] = membershipSummary?.membership_cards ?? cartData?.membership_cards ?? [];
  const pitchingCards: any[] = membershipCards.filter(isPitchCard);
  const usageCards: any[] = membershipCards.filter(isUsageCard);

  useEffect(() => {
    return () => {
      if (suppressEmrScrollSyncRafRef.current !== null) {
        window.cancelAnimationFrame(suppressEmrScrollSyncRafRef.current);
        suppressEmrScrollSyncRafRef.current = null;
      }
    };
  }, []);

  const withEmrScrollSyncSuppressed = (apply: () => void) => {
    suppressEmrScrollSyncRef.current = true;
    if (suppressEmrScrollSyncRafRef.current !== null) {
      window.cancelAnimationFrame(suppressEmrScrollSyncRafRef.current);
    }
    apply();
    suppressEmrScrollSyncRafRef.current = window.requestAnimationFrame(() => {
      suppressEmrScrollSyncRef.current = false;
      suppressEmrScrollSyncRafRef.current = null;
    });
  };

  useEffect(() => {
    setEmrModalOpen(false);
    setEmrId(null);
    setMembershipModalOpen(false);
    setSelectedMembershipId(null);
    setMembershipSummary(null);
    setSelectedPitchingId(null);
    setMembershipSimulation(null);
  }, [cartID]);

  useEffect(() => {
    if (!emrCommand) return;
    if (emrCommand.cartId && String(emrCommand.cartId) !== String(cartID)) return;
    if (emrCommand.action === "OPEN") {
      setMembershipModalOpen(false);
      setSelectedMembershipId(null);
      setSelectedPitchingId(null);
      setEmrId(String(emrCommand.emrId));
      setEmrModalOpen(true);
      return;
    }
    setEmrModalOpen(false);
    setEmrId(null);
  }, [emrCommand, cartID]);

  useEffect(() => {
    if (!membershipCommand) return;
    if (membershipCommand.cartId && String(membershipCommand.cartId) !== String(cartID)) return;
    if (membershipCommand.action === "OPEN") {
      setEmrModalOpen(false);
      setEmrId(null);
      setSelectedPitchingId(null);
      setSelectedMembershipId(String(membershipCommand.userMembershipId));
      setMembershipModalOpen(true);
      return;
    }
    setMembershipModalOpen(false);
    setSelectedMembershipId(null);
  }, [membershipCommand, cartID]);

  useEffect(() => {
    if (!pitchingCommand) return;
    if (pitchingCommand.cartId && String(pitchingCommand.cartId) !== String(cartID)) return;
    if (pitchingCommand.action === "OPEN") {
      setEmrModalOpen(false);
      setEmrId(null);
      setMembershipModalOpen(false);
      setSelectedMembershipId(null);
      setSelectedPitchingId(String(pitchingCommand.membershipId));
      return;
    }
    setSelectedPitchingId(null);
  }, [pitchingCommand, cartID]);

  const getSessionKey = () => (resolvedCustomerId ? `${resolvedCustomerId}-${cartID}-${mappedClinicId}` : null);

  useEffect(() => {
    fetchCartDetailsRef.current = fetchCartDetaills;
  }, [fetchCartDetaills]);

  const fetchCart = useCallback(
    async (force?: boolean) => {
      if (!cartID || !mappedClinicId) {
        setCartData(null);
        setLoading(false);
        prevFetchKeyRef.current = null;
        return;
      }
      const fetchKey = `${cartID}-${mappedClinicId}`;
      if (!force && prevFetchKeyRef.current === fetchKey) return;
      prevFetchKeyRef.current = fetchKey;
      setLoading(true);
      const response = await fetchCartDetailsRef.current(cartID, mappedClinicId);
      if (response) setCartData(response);
      setLoading(false);
    },
    [cartID, mappedClinicId],
  );

  const isScrollLikeMessage = (message: unknown) => {
    if (!message || typeof message !== "object") return false;
    const eventType =
      ("event" in message && message.event) ||
      ("type" in message && message.type) ||
      ("event_type" in message && message.event_type) ||
      null;
    if (eventType === "EMR_ACTION" || eventType === "EMR_SECTION") return false;
    if (eventType === "SCROLL_SYNC") return true;
    const payload = "payload" in message ? message.payload : message;
    if (!payload || typeof payload !== "object") return false;
    if ("action" in payload && payload.action === "EMR_SCROLL") return false;
    return "scrollTop" in payload || "scroll_top" in payload || "scrollHeight" in payload || "scroll_height" in payload;
  };

  const handleEmrMessage = (message: any) => {
    if (!message || typeof message !== "object") return false;
    const eventType =
      ("event" in message && message.event) ||
      ("type" in message && message.type) ||
      ("event_type" in message && message.event_type) ||
      null;
    const payload = "payload" in message ? message.payload : message;
    if (!payload || typeof payload !== "object") return false;
    const action = "action" in payload ? payload.action : null;
    const actionId =
      ("action_id" in payload && payload.action_id) ||
      ("actionId" in payload && payload.actionId) ||
      ("emr_id" in payload && payload.emr_id) ||
      ("emrId" in payload && payload.emrId) ||
      null;
    if (eventType === "CART_SCROLL_STEP" || action === "CART_SCROLL_STEP") {
      const messageCartId = (payload as any).cart_id ?? (payload as any).cartId ?? null;
      if (messageCartId !== null && messageCartId !== undefined && cartID && String(messageCartId) !== String(cartID)) {
        return true;
      }
      const innerTarget = selectedPitchingId ? membershipDetailScrollRef.current : mirrorScrollRef.current;
      const outerTarget = selectedPitchingId ? null : mirrorContainerRef.current;
      const target =
        innerTarget && innerTarget.scrollHeight > innerTarget.clientHeight + 1
          ? innerTarget
          : outerTarget && outerTarget.scrollHeight > outerTarget.clientHeight + 1
            ? outerTarget
            : (innerTarget ?? outerTarget);
      if (!target) return true;
      const toNumber = (value: unknown) => {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        if (typeof value === "string") {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
      };
      const deltaValue = toNumber((payload as any).delta ?? (payload as any).scrollDelta ?? (payload as any).step);
      const direction = (payload as any).direction;
      const delta = deltaValue !== null ? deltaValue : direction === "up" ? -240 : direction === "down" ? 240 : 240;
      target.scrollBy({ top: delta, behavior: "smooth" });
      return true;
    }
    const isEmrEvent =
      eventType === "EMR_SECTION" ||
      eventType === "EMR_ACTION" ||
      action === "EMR_OPEN" ||
      action === "EMR_CLOSE" ||
      action === "EMR_SCROLL" ||
      action === "EMR_SCROLL_STEP";
    if (!isEmrEvent) return false;
    const messageCartId = (payload as any).cart_id ?? (payload as any).cartId ?? null;
    if (messageCartId !== null && messageCartId !== undefined && cartID && String(messageCartId) !== String(cartID)) {
      return true;
    }
    if (
      (action === "EMR_SCROLL" || action === "EMR_SCROLL_STEP") &&
      actionId &&
      emrId &&
      String(actionId) !== String(emrId)
    ) {
      return true;
    }
    if (action === "EMR_SCROLL") {
      const target = emrScrollRef.current;
      if (!target) return true;
      const toNumber = (value: unknown) => {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        if (typeof value === "string") {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
      };
      const scrollTopValue = toNumber(payload.scrollTop ?? payload.scroll_top);
      const scrollHeightValue = toNumber(payload.scrollHeight ?? payload.scroll_height);
      const clientHeightValue = toNumber(payload.clientHeight ?? payload.client_height);
      if (scrollTopValue === null || scrollHeightValue === null || clientHeightValue === null) return true;
      const maxScrollTop = Math.max(0, target.scrollHeight - target.clientHeight);
      const denom = Math.max(1, scrollHeightValue - clientHeightValue);
      const nextScrollTop = (scrollTopValue / denom) * maxScrollTop;
      withEmrScrollSyncSuppressed(() => {
        target.scrollTo({ top: Math.min(nextScrollTop, maxScrollTop), behavior: "auto" });
      });
      return true;
    }
    if (action === "EMR_SCROLL_STEP") {
      const target = emrScrollRef.current;
      if (!target) return true;
      const toNumber = (value: unknown) => {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        if (typeof value === "string") {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
      };
      const deltaValue = toNumber((payload as any).delta ?? (payload as any).scrollDelta ?? (payload as any).step);
      const direction = (payload as any).direction;
      const delta = deltaValue !== null ? deltaValue : direction === "up" ? -240 : direction === "down" ? 240 : 240;
      withEmrScrollSyncSuppressed(() => {
        target.scrollBy({ top: delta, behavior: "auto" });
      });
      return true;
    }
    if (action === "EMR_OPEN") {
      if (actionId) {
        setEmrId(String(actionId));
      }
      setMembershipModalOpen(false);
      setSelectedMembershipId(null);
      setSelectedPitchingId(null);
      setEmrModalOpen(true);
    }
    if (action === "EMR_CLOSE") {
      setEmrModalOpen(false);
      setEmrId(null);
    }
    return true;
  };

  const handleMembershipMessage = (message: any) => {
    if (!message || typeof message !== "object") return false;
    const eventType = message.event ?? message.type ?? message.event_type ?? null;
    if (!eventType || String(eventType).toLowerCase() !== "membership") return false;
    setMembershipSummary(message);
    return true;
  };
  
  const handleMembershipSimulationMessage = (message: any) => {
    const eventType = message?.event_type;
    if (!Object.values(MEMBERSHIP_SIMULATION_EVENTS).includes(eventType)) return false;
    if (lastSimulationMessageRef.current === message) return true;
    lastSimulationMessageRef.current = message;
    const eventData = message.payload ?? message;
    if (eventData?.cart_id && cartID && String(eventData.cart_id) !== String(cartID)) return true;
    if (eventType === MEMBERSHIP_SIMULATION_EVENTS.CLOSE) {
      setMembershipSimulation(null);
    } else if (eventType === MEMBERSHIP_SIMULATION_EVENTS.SCROLL) {
      simulationScrollRef.current?.scrollBy({ top: Number(eventData?.delta) || 0, behavior: "smooth" });
    } else if (eventData?.pets || eventData?.hd_cart) {
      if (source !== "ACTION") {
        logAPI({
          event_type: "MIRROR_MEMBERSHIP_SIMULATION_VIEWED",
          scr_id: "mirror-view",
          client_id: resolvedCustomerId,
          cart_id: cartID,
          clinic_id: mappedClinicId,
          clinic_name: mappedClinicName,
          vms_user_id: userEmailId,
          membership_id: eventData?.membership_id,
          membership_name: eventData?.membership_name ?? "",
          membership_details: {
            savings_on_cart: eventData?.savings_on_cart,
            total_price_without_membership: eventData?.total_price_without_membership,
            total_price_with_membership: eventData?.total_price_with_membership,
          },
          timestamp: Date.now(),
        });
      }
      setEmrModalOpen(false);
      setMembershipModalOpen(false);
      setSelectedPitchingId(null);
      setMembershipSimulation(eventData);
    }
    return true;
  };

  useEffect(() => {
    if (emrModalOpen || membershipModalOpen || selectedPitchingId) setMembershipSimulation(null);
  }, [emrModalOpen, membershipModalOpen, selectedPitchingId]);

  const handlePitchingActionMessage = (message: any) => {
    if (!message || typeof message !== "object") return false;
    const payload = "payload" in message ? message.payload : message;
    if (!payload || typeof payload !== "object") return false;
    const action = "action" in payload ? (payload as any).action : null;
    if (action !== "MEMBERSHIP_OPEN" && action !== "MEMBERSHIP_CLOSE") return false;
    const messageCartId = (payload as any).cart_id ?? (payload as any).cartId ?? null;
    if (messageCartId !== null && messageCartId !== undefined && cartID && String(messageCartId) !== String(cartID)) {
      return false;
    }
    if (action === "MEMBERSHIP_CLOSE") {
      setSelectedPitchingId(null);
      return false;
    }
    const membershipId =
      ("action_id" in payload && (payload as any).action_id) ||
      ("membership_id" in payload && (payload as any).membership_id) ||
      null;
    const isPitchingCard = pitchingCards.some((card: any) => String(card?.membership_id) === String(membershipId));
    if (!membershipId || !isPitchingCard) return false;
    setEmrModalOpen(false);
    setEmrId(null);
    setMembershipModalOpen(false);
    setSelectedMembershipId(null);
    setSelectedPitchingId(String(membershipId));
    return true;
  };

  const handleMembershipActionMessage = (message: any) => {
    if (!message || typeof message !== "object") return false;
    const eventType =
      ("event" in message && message.event) ||
      ("type" in message && message.type) ||
      ("event_type" in message && message.event_type) ||
      null;
    const payload = "payload" in message ? message.payload : message;
    if (!payload || typeof payload !== "object") return false;
    const action = "action" in payload ? (payload as any).action : null;
    const isMembershipActionEvent = String(eventType ?? "").toLowerCase() === "membership_action";
    const isMembershipAction =
      isMembershipActionEvent ||
      action === "MEMBERSHIP_OPEN" ||
      action === "MEMBERSHIP_CLOSE" ||
      action === "MEMBERSHIP_SCROLL";
    if (!isMembershipAction) return false;
    const messageCartId = (payload as any).cart_id ?? (payload as any).cartId ?? null;
    if (messageCartId !== null && messageCartId !== undefined && cartID && String(messageCartId) !== String(cartID)) {
      return true;
    }
    if (action === "MEMBERSHIP_OPEN") {
      const membershipId =
        ("action_id" in payload && (payload as any).action_id) ||
        ("actionId" in payload && (payload as any).actionId) ||
        ("user_membership_id" in payload && (payload as any).user_membership_id) ||
        ("userMembershipId" in payload && (payload as any).userMembershipId) ||
        null;
      setEmrModalOpen(false);
      setEmrId(null);
      setSelectedPitchingId(null);
      setSelectedMembershipId(membershipId ? String(membershipId) : null);
      setMembershipModalOpen(true);
      return true;
    }
    if (action === "MEMBERSHIP_CLOSE") {
      setMembershipModalOpen(false);
      setSelectedMembershipId(null);
      return true;
    }
    if (action === "MEMBERSHIP_SCROLL") {
      const delta = (payload as any).delta ?? ((payload as any).direction === "up" ? -240 : 240);
      if (membershipModalOpen || selectedPitchingId) {
        membershipDetailScrollRef.current?.scrollBy({ top: delta, behavior: "smooth" });
      } else {
        membershipDemoScrollRef.current?.scrollBy({ top: delta, behavior: "smooth" });
      }
      return true;
    }
    return true;
  };

  useEffect(() => {
    if (handleMembershipSimulationMessage(socketJsonMessage)) {
      return;
    }
    if (handlePitchingActionMessage(socketJsonMessage)) {
      return;
    }
    if (handleMembershipActionMessage(socketJsonMessage)) {
      return;
    }
    if (handleMembershipMessage(socketJsonMessage)) {
      return;
    }
    const data = socketRawMessage?.data;
    if (!data) return;
    const parseText = (rawText: string) => {
      const raw = rawText.trim();
      if (!raw || raw === "pong") return;
      try {
        const parsed = JSON.parse(raw);
        if (handleMembershipSimulationMessage(parsed)) return;
        if (handlePitchingActionMessage(parsed)) return;
        if (handleMembershipActionMessage(parsed)) return;
        if (handleMembershipMessage(parsed)) return;
      } catch {
        return;
      }
    };
    if (typeof data === "string") {
      parseText(data);
      return;
    }
    if (data instanceof Blob) {
      data
        .text()
        .then(parseText)
        .catch((error) => {
          console.error("[CartUpdate] Failed to parse blob:", error);
        });
      return;
    }
    if (data instanceof ArrayBuffer) {
      parseText(new TextDecoder().decode(data));
      return;
    }
    if (ArrayBuffer.isView(data)) {
      parseText(new TextDecoder().decode(data));
    }
  }, [socketJsonMessage, socketRawMessage]);

  useEffect(() => {
    if (handleEmrMessage(socketJsonMessage)) {
      return;
    }
    const data = socketRawMessage?.data;
    if (!data) return;
    const parseText = (rawText: string) => {
      const raw = rawText.trim();
      if (!raw || raw === "pong") return;
      try {
        const parsed = JSON.parse(raw);
        if (handleEmrMessage(parsed)) return;
      } catch {
        return;
      }
    };
    if (typeof data === "string") {
      parseText(data);
      return;
    }
    if (data instanceof Blob) {
      data
        .text()
        .then(parseText)
        .catch((error) => {
          console.error("[EMRMessage] Failed to parse blob:", error);
        });
      return;
    }
    if (data instanceof ArrayBuffer) {
      parseText(new TextDecoder().decode(data));
      return;
    }
    if (ArrayBuffer.isView(data)) {
      parseText(new TextDecoder().decode(data));
    }
  }, [socketJsonMessage, socketRawMessage]);

  useEffect(() => {
    if (socketJsonMessage && isScrollLikeMessage(socketJsonMessage)) {
      setResolvedScrollMessage(socketJsonMessage);
      return;
    }
    const data = socketRawMessage?.data;
    if (!data) return;
    const parseText = (rawText: string) => {
      const raw = rawText.trim();
      if (!raw || raw === "pong") return;
      try {
        const parsed = JSON.parse(raw);
        if (isScrollLikeMessage(parsed)) {
          setResolvedScrollMessage(parsed);
        }
      } catch {
        return;
      }
    };
    if (typeof data === "string") {
      parseText(data);
      return;
    }
    if (data instanceof Blob) {
      data
        .text()
        .then(parseText)
        .catch((error) => {
          console.error("[ScrollMessage-Parsing] Failed to parse blob:", error);
        });
      return;
    }
    if (data instanceof ArrayBuffer) {
      parseText(new TextDecoder().decode(data));
      return;
    }
    if (ArrayBuffer.isView(data)) {
      parseText(new TextDecoder().decode(data));
    }
  }, [socketJsonMessage, socketRawMessage]);

  const extractCartUpdate = (message: unknown): ICartDetailsResponseData | null => {
    if (!message || typeof message !== "object") return null;
    const payload = "data" in message ? (message as any).data : message;
    if (!payload || typeof payload !== "object") return null;
    if (!("pets" in payload) && !("price_breakup" in payload) && !("hd_cart" in payload)) return null;
    return payload as ICartDetailsResponseData;
  };

  const applyCartUpdate = (incoming: ICartDetailsResponseData) => {
    const incomingCartId = (incoming as any)?.cart_id ?? null;
    if (cartID && incomingCartId && String(incomingCartId) !== String(cartID)) return;
    const hasField = (field: keyof ICartDetailsResponseData) =>
      Object.prototype.hasOwnProperty.call(incoming as unknown as Record<string, unknown>, field);
    setCartData((prev) => {
      if (!prev) return incoming;
      const shouldClearHdCart =
        !hasField("hd_cart") &&
        hasField("price_breakup") &&
        Number((incoming as any)?.price_breakup?.home_delivery_total ?? 0) === 0;
      return {
        ...prev,
        pets: hasField("pets") ? incoming.pets : prev.pets,
        price_breakup: hasField("price_breakup") ? incoming.price_breakup : prev.price_breakup,
        hd_cart: hasField("hd_cart") ? incoming.hd_cart : shouldClearHdCart ? null : prev.hd_cart,
      };
    });
    setLoading(false);
  };

  useEffect(() => {
    if (cartData?.membership) {
      setMembershipSummary({
        event_type: "membership",
        ...cartData.membership,
      });
    }
  }, [cartData?.membership]);

  useEffect(() => {
    const incoming = extractCartUpdate(socketJsonMessage);
    if (incoming) {
      applyCartUpdate(incoming);
      return;
    }
    const data = socketRawMessage?.data;
    if (!data) return;
    const parseText = (rawText: string) => {
      const raw = rawText.trim();
      if (!raw || raw === "pong") return;
      try {
        const parsed = JSON.parse(raw);
        const parsedIncoming = extractCartUpdate(parsed);
        if (parsedIncoming) {
          applyCartUpdate(parsedIncoming);
        }
      } catch {
        return;
      }
    };
    if (typeof data === "string") {
      parseText(data);
      return;
    }
    if (data instanceof Blob) {
      data
        .text()
        .then(parseText)
        .catch((error) => {
          console.error("[ScrollMessage] Failed to parse blob:", error);
        });
      return;
    }
    if (data instanceof ArrayBuffer) {
      parseText(new TextDecoder().decode(data));
      return;
    }
    if (ArrayBuffer.isView(data)) {
      parseText(new TextDecoder().decode(data));
    }
  }, [cartID, socketJsonMessage, socketRawMessage]);

  useEffect(() => {
    if (!resolvedScrollMessage || typeof resolvedScrollMessage === "string") return;
    if (selectedPitchingId) return;
    const eventType =
      ("event" in resolvedScrollMessage && resolvedScrollMessage.event) ||
      ("type" in resolvedScrollMessage && resolvedScrollMessage.type) ||
      ("event_type" in resolvedScrollMessage && resolvedScrollMessage.event_type) ||
      null;
    const payload = "payload" in resolvedScrollMessage ? resolvedScrollMessage.payload : resolvedScrollMessage;
    if (!payload || typeof payload !== "object") return;
    const isImplicitScrollSync =
      eventType === null &&
      ("scrollTop" in payload || "scroll_top" in payload || "scrollHeight" in payload || "scroll_height" in payload);
    if (eventType !== "SCROLL_SYNC" && !isImplicitScrollSync) return;
    const incomingCartId = payload.cart_id ?? payload.cartId ?? null;
    if (cartID && incomingCartId && String(incomingCartId) !== String(cartID)) return;
    const target = mirrorScrollRef.current;
    if (!target) return;
    const maxScrollTop = Math.max(0, target.scrollHeight - target.clientHeight);
    const toNumber = (value: unknown) => {
      if (typeof value === "number" && Number.isFinite(value)) return value;
      if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };
    const progress = toNumber(payload.progress);
    const scrollTopValue = toNumber(payload.scrollTop ?? payload.scroll_top ?? payload.scrollTop);
    const scrollHeightValue = toNumber(payload.scrollHeight ?? payload.scroll_height);
    const clientHeightValue = toNumber(payload.clientHeight ?? payload.client_height);
    let nextScrollTop: number | null = null;
    if (progress !== null) {
      nextScrollTop = progress * maxScrollTop;
    } else if (scrollTopValue !== null && scrollHeightValue !== null && clientHeightValue !== null) {
      const denom = Math.max(1, scrollHeightValue - clientHeightValue);
      nextScrollTop = (scrollTopValue / denom) * maxScrollTop;
    } else if (scrollTopValue !== null) {
      nextScrollTop = scrollTopValue;
    }
    if (nextScrollTop === null) return;
    target.scrollTo({ top: Math.min(nextScrollTop, maxScrollTop), behavior: "auto" });
  }, [resolvedScrollMessage, cartID, selectedPitchingId]);

  useEffect(() => {
    if (!cartID) return;
    const logBody: IClickLogBody = {
      event_type: "MIRROR_SCRENN_CART_SUMMARY",
      client_id: resolvedCustomerId,
      cart_id: cartID,
      scr_id: "mirror-view",
      clinic_id: mappedClinicId,
      vms_user_id: userEmailId,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  }, [cartID, logAPI, mappedClinicId, resolvedCustomerId, userEmailId]);
  const selectedPitchingCard = selectedPitchingId
    ? pitchingCards.find((card: any) => String(card?.membership_id) === String(selectedPitchingId))
    : undefined;

  const selectedMembershipEntry = membershipModalOpen
    ? usageCards.find(
        (entry: any) => String(entry?.user_membership_id) === String(selectedMembershipId),
      )
    : undefined;

  useEffect(() => {
    if (source !== "ACTION") return;
    const card = selectedPitchingCard;
    const entry = selectedMembershipEntry;
    if (!card && !entry) return;
    logAPI({
      event_type: card ? "PITCHING_DETAIL_OPEN" : "MEMBERSHIP_DETAIL_OPEN",
      client_id: resolvedCustomerId,
      cart_id: cartID,
      scr_id: "mirror-view",
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userEmailId,
      membership_id: String(card ? selectedPitchingId : selectedMembershipId),
      membership_name: (card ?? entry).category ?? "",
      membership_tag: card
        ? card.is_top_best_match
          ? "BEST_MATCH"
          : card.is_recommended
            ? "RECOMMENDED"
            : "NONE"
        : entry.is_renew_now
          ? "RENEW_NOW"
          : entry.is_recommended
            ? "RECOMMENDED"
            : "NONE",
      timestamp: Date.now(),
    });
  }, [source, selectedPitchingId, selectedMembershipId, membershipModalOpen, cartID]);

  useEffect(() => {
    const sessionKey = getSessionKey();
    if (!sessionKey) return;
    if (activeSessionKeyRef.current !== sessionKey) {
      activeSessionKeyRef.current = sessionKey;
      fetchCart(false);
    }
  }, [resolvedCustomerId, cartID, mappedClinicId, fetchCart]);

  return loading ? (
    <CartMirrorComponent />
  ) : (
    <div
      ref={mirrorContainerRef}
      className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 p-4 h-screen overflow-y-auto"
    >
      <div>
        <BillingMirrorHeader clinicName={mappedClinicName} />
        <div className="flex">
          <div className="flex-1">
            <StickyTotalAmountBar
              finalAmount={cartData?.price_breakup?.net_total_after_tax ?? 0}
              originalAmount={cartData?.price_breakup?.total_gross_price}
              savings={cartData?.price_breakup?.total_discount}
            />
            <BillingMirrorItems cartData={cartData} scrollRef={mirrorScrollRef} hasMembership={true} />
          </div>
          {cartData?.membership || membershipCards.length > 0 ? (
            <MembershipPlans
              membershipSummary={membershipSummary}
              cards={membershipCards}
              scrollRef={membershipDemoScrollRef}
            />
          ) : null}
        </div>
      </div>
      {emrModalOpen && emrId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-lg">
            <EmrDetailsComponent
              ref={emrScrollRef}
              emr_id={emrId}
              enableScrollSync={true}
              cartId={cartID}
              suppressScrollSyncRef={suppressEmrScrollSyncRef}
            />
          </div>
        </div>
      )}
      {selectedPitchingCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            className="relative w-full max-w-[1580px] max-h-[800px] rounded-[40px] overflow-hidden
              shadow-[4px_8px_32px_0px_rgba(0,0,0,0.05)] bg-white
              bg-[url('/background_membership.svg')] bg-[length:110%_auto] bg-center flex flex-col"
          >
            <PitchingDetailComponent
              card={selectedPitchingCard}
              scrollRef={membershipDetailScrollRef}
            />
          </div>
        </div>
      )}
      {membershipSimulation && (
        <MembershipSimulationPopup simulation={membershipSimulation} scrollRef={simulationScrollRef} />
      )}
      {membershipModalOpen && selectedMembershipId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-[1580px] h-[800px] rounded-[32px] overflow-hidden bg-[url('/background_membership.svg')] bg-cover bg-center flex flex-col">
            <MembershipDetailComponent
              membershipId={selectedMembershipId}
              membershipCards={usageCards}
              scrollRef={membershipDetailScrollRef}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingViewComponent;
